<template>
  <div class="lg:ml-5 justify-content-around py-2 gap-2 max-h-full overflow-hidden">
    <Breadcrumb
      :home="home"
      :model="items"
    />

    <div
      v-if="loading"
      class="flex justify-center items-center p-6"
    >
      <ProgressSpinner class="w-8 h-8" />
    </div>

    <div
      v-else-if="error"
      class="bg-red-50 p-4 rounded-xl m-6"
    >
      <p class="text-red-600">
        {{ error }}
      </p>
    </div>

    <div
      v-else
      class="bg-white rounded-xl shadow m-6"
    >
      <!-- Header -->
      <div class="flex justify-between items-center p-6 border-b border-gray-200">
        <h1 class="text-2xl font-bold text-gray-800">
          {{ part?.name || 'Loading...' }}
        </h1>
        <Button
          label="Re-order"
          icon="pi pi-shopping-cart"
          class="p-button-primary"
          @click="handleReorder"
        />
      </div>

      <!-- Part Details -->
      <div class="grid grid-cols-2 gap-6 p-6">
        <div class="space-y-4">
          <div>
            <h2 class="text-lg font-semibold mb-2">
              Part Information
            </h2>
            <div class="space-y-2">
              <div class="flex justify-between">
                <span class="text-gray-600">Part Number:</span>
                <span class="font-medium">{{ part?.partNumber }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600">Current Stock:</span>
                <span class="font-medium">{{ part?.currentStock }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600">Minimum Stock:</span>
                <span class="font-medium">{{ part?.minStock }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600">Location:</span>
                <span class="font-medium">{{ part?.location }}</span>
              </div>
            </div>
          </div>

          <div>
            <h2 class="text-lg font-semibold mb-2">
              Supplier Information
            </h2>
            <div class="space-y-2">
              <div class="flex justify-between">
                <span class="text-gray-600">Supplier:</span>
                <span class="font-medium">{{ part?.supplier?.name }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600">Supplier Part Number:</span>
                <span class="font-medium">{{ part?.supplier?.partNumber }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600">Last Order Date:</span>
                <span class="font-medium">{{ part?.lastOrderDate ? formatDate(part.lastOrderDate) : '-'
                }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600">Unit Price:</span>
                <span class="font-medium">{{ part?.unitPrice ? `£${part.unitPrice.toFixed(2)}` : '-'
                }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="space-y-4">
          <div>
            <h2 class="text-lg font-semibold mb-2">
              Usage History
            </h2>
            <Chart
              type="line"
              :data="usageData"
              :options="chartOptions"
              class="h-64"
            />
          </div>

          <div>
            <h2 class="text-lg font-semibold mb-2">
              Notes
            </h2>
            <div class="bg-gray-50 p-4 rounded-lg">
              <p class="text-gray-700">
                {{ part?.notes || 'No notes available' }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Re-order Dialog -->
    <Dialog
      v-model:visible="showReorderDialog"
      modal
      header="Re-order Parts"
      class="w-[30rem]"
    >
      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
          <InputNumber
            v-model="reorderQuantity"
            :min="1"
            class="w-full"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <Textarea
            v-model="reorderNotes"
            rows="3"
            class="w-full"
          />
        </div>
      </div>
      <template #footer>
        <div class="flex justify-end gap-2">
          <Button
            label="Cancel"
            class="p-button-text"
            @click="showReorderDialog = false"
          />
          <Button
            label="Submit Order"
            class="p-button-primary"
            @click="submitReorder"
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';

const route = useRoute();
const part = ref({});
const loading = ref(true);
const error = ref(null);
const showReorderDialog = ref(false);
const reorderQuantity = ref(1);
const reorderNotes = ref('');

// Breadcrumb configuration
const home = ref({
    icon: 'pi pi-home',
    url: '/workshop'
});

const items = ref([
    {
        label: 'Parts',
        url: '/workshop/parts'
    },
    {
        label: 'Part Details'
    }
]);

// Mock data for parts
const mockParts = {
    'P001': {
        partNumber: 'P001',
        name: 'LED Par Can Lens',
        currentStock: 5,
        minStock: 10,
        location: 'Shelf A1',
        supplier: {
            name: 'Lighting Solutions Ltd',
            partNumber: 'LS-PAR-001'
        },
        lastOrderDate: '2024-02-15',
        unitPrice: 45.99,
        notes: 'Compatible with all MK2 LED Par Cans'
    },
    'P002': {
        partNumber: 'P002',
        name: 'DMX Cable 5m',
        currentStock: 12,
        minStock: 20,
        location: 'Shelf B2',
        supplier: {
            name: 'Cable Specialists',
            partNumber: 'DMX-5M-PRO'
        },
        lastOrderDate: '2024-03-01',
        unitPrice: 15.50,
        notes: 'Professional grade, neutrik connectors'
    }
};

// Mock usage history data
const mockUsageHistory = {
    'P001': [
        { month: 'Jan', quantity: 3 },
        { month: 'Feb', quantity: 5 },
        { month: 'Mar', quantity: 2 },
        { month: 'Apr', quantity: 4 },
        { month: 'May', quantity: 6 },
        { month: 'Jun', quantity: 3 }
    ],
    'P002': [
        { month: 'Jan', quantity: 8 },
        { month: 'Feb', quantity: 6 },
        { month: 'Mar', quantity: 10 },
        { month: 'Apr', quantity: 4 },
        { month: 'May', quantity: 7 },
        { month: 'Jun', quantity: 5 }
    ]
};

// Chart data
const usageData = ref({
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [{
        label: 'Monthly Usage',
        data: [0, 0, 0, 0, 0, 0],
        borderColor: '#FFA726',
        tension: 0.4
    }]
});

const chartOptions = {
    plugins: {
        legend: {
            display: false
        }
    },
    scales: {
        y: {
            beginAtZero: true
        }
    }
};

onMounted(() => {
    try {
        loading.value = true;
        error.value = null;

        // Get mock part data
        const mockPart = mockParts[route.params.id];
        if (!mockPart) {
            throw new Error('Part not found');
        }
        part.value = mockPart;

        // Update breadcrumb with part name
        items.value[1].label = mockPart.name;

        // Get mock usage history
        const mockUsage = mockUsageHistory[route.params.id];
        if (mockUsage) {
            usageData.value.labels = mockUsage.map(h => h.month);
            usageData.value.datasets[0].data = mockUsage.map(h => h.quantity);
        }
    } catch (err) {
        error.value = err.message;
        console.error('Error loading mock data:', err);
    } finally {
        loading.value = false;
    }
});

const handleReorder = () => {
    if (!part.value) return;
    reorderQuantity.value = Math.max(1, part.value.minStock - part.value.currentStock);
    showReorderDialog.value = true;
};

const submitReorder = async () => {
    try {
        // Mock successful reorder
        console.log('Reorder submitted:', {
            partId: route.params.id,
            quantity: reorderQuantity.value,
            notes: reorderNotes.value
        });
        showReorderDialog.value = false;
        // Could add a success message here
    } catch (err) {
        console.error('Error submitting reorder:', err);
    }
};

const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
    });
};
</script>